// Events.jsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// === HERO IMAGE ===
import eventsHeroImage from '../assets/pick/8.jpg';

// === EVENT IMAGES ===
import event1 from '../assets/pick/9.jpeg';
import event4 from '../assets/pick/10.jpeg';
import event2 from '../assets/pick/11.jpeg';
import event5 from '../assets/pick/12.jpg';
import event6 from '../assets/pick/15.jpg';
import event8 from '../assets/pick/16.jpeg';
import event3 from '../assets/pick/17.jpg';
import event7 from '../assets/pick/18.webp';

// === FRAMER MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};

const Events = () => {
    const { t } = useTranslation();
    const [index, setIndex] = useState(-1);
    const location = useLocation();

    // === EVENTS DATA (Tradotto con t()) ===
    const events = [
        { image: event1, label: t('events.labels.kandyPerahera'), span: "col-span-1 row-span-1" },
        { image: event4, label: t('events.labels.vesak'), span: "col-span-1 row-span-2" },
        { image: event2, label: t('events.labels.sinhalaTamilNewYear'), span: "col-span-1 row-span-1" },
        { image: event5, label: t('events.labels.christmas'), span: "col-span-1 row-span-2" },
        { image: event6, label: t('events.labels.thaiPongal'), span: "col-span-1 row-span-2" },
        { image: event8, label: t('events.labels.deepavali'), span: "col-span-1 row-span-2" },
        { image: event3, label: t('events.labels.holi'), span: "col-span-1 row-span-1" },
        { image: event7, label: t('events.labels.poson'), span: "col-span-1 row-span-1" },
    ];

    // Prepare slides for Lightbox
    const slides = events.map((item, i) => ({
        src: item.image,
        width: 1600,
        height: 1067,
        alt: item.label,
        title: item.label,
    }));

    return (
        <div className="bg-black">
            {/* ===== HERO SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative h-[700px] w-full bg-cover bg-left md:h-screen md:max-h-[740px] md:bg-center lg:max-h-[800px] xl:max-h-[960px]"
                style={{ backgroundImage: `url(${eventsHeroImage})` }}
            >
                <div className="container mx-auto px-4 h-full">
                    <div className="relative flex h-full w-full justify-start pt-[150px] md:items-center">
                        <div className="mt-32 flex flex-col items-start md:mt-0">
                            {/* Gradient Heading */}
                            <h1
                                className="font-konkhmer text-[3.125rem] uppercase md:text-5xl lg:text-[5rem] font-bold"
                                style={{
                                    background: 'linear-gradient(to right, #009CE6, #96BA33)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('events.pageTitle')}
                            </h1>

                            <div className="relative mt-3 lg:mt-3">
                                <p className="max-w-[245px] text-left font-inknut text-xs leading-6 text-white md:max-w-[300px] md:text-sm lg:max-w-[670px] lg:leading-8 xl:text-lg">
                                    {t('events.subtitle')}
                                </p>

                                {/* Gradient Accent Line */}
                                <div
                                    className="absolute -bottom-5 left-0 h-[5px] w-[100px] md:w-[120px]"
                                    style={{
                                        background: 'linear-gradient(to right, #009CE6, #96BA33)',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== EVENTS GRID SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-black py-20 md:py-16 lg:py-24 xl:py-32"
            >
                <div className="container mx-auto px-4">
                    {/* Tabs */}
                    <div className="flex items-center justify-center gap-3 font-redHatDisplay text-2xl font-bold uppercase md:text-4xl">
                        <Link
                            to="/gallery"
                            className={`border-b-[3px] pb-1 transition-all duration-300 ${location.pathname === "/gallery"
                                ? "border-[#009CE6] text-[#009CE6]"
                                : "border-transparent text-white hover:text-[#96BA33]"
                                }`}
                        >
                            {t('events.tabs.places')}
                        </Link>
                        <span className="text-white">/</span>
                        <Link
                            to="/events"
                            className={`border-b-[3px] pb-1 transition-all duration-300 ${location.pathname === "/events"
                                ? "border-[#96BA33] text-[#96BA33]"
                                : "border-transparent text-white hover:text-[#009CE6]"
                                }`}
                        >
                            {t('events.tabs.events')}
                        </Link>
                    </div>

                    {/* Grid Container */}
                    <div className="mt-16 grid auto-rows-[200px] grid-cols-2 items-stretch gap-2 md:auto-rows-[250px] md:grid-cols-3 md:gap-3 lg:auto-rows-[450px]">
                        {events.map((item, i) => (
                            <motion.button
                                key={i}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.98 }}
                                type="button"
                                onClick={() => setIndex(i)}
                                className={`group relative cursor-pointer overflow-hidden rounded-lg ${item.span} transition-all duration-300 hover:shadow-2xl`}
                                aria-label={t('events.lightboxAria', { number: i + 1 })}
                            >
                                <img
                                    src={item.image}
                                    alt={item.label}
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    loading="lazy"
                                />

                                {/* Label overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="absolute bottom-0 left-0 right-0 p-4">
                                        <h3 className="font-konkhmer text-sm font-bold uppercase tracking-wider text-white md:text-xl">
                                            {item.label}
                                        </h3>
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* === LIGHTBOX === */}
                <Lightbox
                    open={index >= 0}
                    close={() => setIndex(-1)}
                    index={index}
                    slides={slides}
                    plugins={[Thumbnails, Zoom]}
                    thumbnails={{
                        position: "bottom",
                        width: 120,
                        height: 80,
                        border: 2,
                        borderColor: "#009CE6",
                        borderRadius: 6,
                        padding: 4,
                        gap: 8,
                    }}
                    zoom={{
                        maxZoomPixelRatio: 3,
                    }}
                    styles={{
                        container: { backgroundColor: "rgba(0, 0, 0, 0.95)" },
                        button: { color: "#fff" },
                        thumbnail: {
                            cursor: "pointer",
                            borderColor: "#009CE6",
                            "&:hover": { borderColor: "#96BA33" }
                        },
                    }}
                    controller={{ closeOnBackdropClick: true }}
                />
            </motion.section>
        </div>
    );
};

export default Events;